import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { PageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components';
import { Box, CircularProgress, Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { EmptyState } from 'src/components/atlas/molecules';
import { useGetSanitizedAndDeletedCasesQuery } from '../../../../graphql/generated';
import { ExpandableCard, SanitizedCaseExpanded } from '../../molecules';
import { DeletedSanitizedCasesFilters } from '../../organisms/DeletedSanitizedCasesFilters/DeletedSanitizedCasesFilters';
import {
  CaseStatus,
  DeletedSanitizedCase,
  filterCases,
  transformCase,
} from '../../utils';
import { ExportLogButton } from './ExportLogButton';

const getStatusChipSx = (
  status: CaseStatus,
  tokens: ReturnType<typeof useTheme<LensTheme>>['tokens'],
) => {
  if (status === CaseStatus.SANITIZED) {
    return {
      backgroundColor: tokens.semantic.color.status.success.background.value,
      color: tokens.semantic.color.status.success.text.value,
      border: 'none',
    };
  }
  return {
    backgroundColor: tokens.semantic.color.status.error.background.value,
    color: tokens.semantic.color.status.error.text.value,
    border: 'none',
  };
};

const DeletedSanitizedCases: React.FC = () => {
  const { tokens } = useTheme<LensTheme>();
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const { data, isLoading, isError, refetch } =
    useGetSanitizedAndDeletedCasesQuery();

  if (!tokens) return null;

  const handleToggle = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const transformedCases = (data?.sanitizedAndDeletedCases || []).map(
    transformCase,
  );

  const filteredCases = filterCases(transformedCases, {
    searchQuery,
    typeFilter,
    countryFilter,
  });

  const uniqueCountries = Array.from(
    new Set(transformedCases.map((c) => c.metadata.country)),
  );

  return (
    <Box sx={{ p: tokens.core.spacing[4].value }}>
      <PageHeader
        pageTitle="Deleted / sanitized Cases"
        pageSubtitle="Cases handled by a data retention policy. Superadmins can restore cases for further jurisdiction review."
        moreButton={<ExportLogButton />}
      />

      <DeletedSanitizedCasesFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        countryFilter={countryFilter}
        onCountryFilterChange={setCountryFilter}
        countries={uniqueCountries}
      />

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: tokens.core.spacing[6].value,
          }}
        >
          <CircularProgress data-testid="deleted-sanitized-cases-loading" />
        </Box>
      ) : isError ? (
        <EmptyState
          title="Error loading cases"
          description="There was an issue retrieving deleted or sanitized cases. Please try again."
        />
      ) : filteredCases.length === 0 ? (
        <EmptyState
          title="No deleted or sanitized cases available"
          description="You have currently no deleted or sanitized cases in the system, or no cases match your filters."
        />
      ) : (
        <Stack sx={{ gap: tokens.core.spacing[2].value }}>
          {filteredCases.map((caseItem: DeletedSanitizedCase) => (
            <ExpandableCard<DeletedSanitizedCase>
              key={caseItem.id}
              toggleKey={caseItem.id}
              title={caseItem.title}
              items={[caseItem]}
              isExpanded={!!expandedKeys[caseItem.id]}
              onToggle={handleToggle}
              chipLabel={caseItem.status}
              chipSx={getStatusChipSx(caseItem.status, tokens)}
              renderExpandedContent={(items) => (
                <SanitizedCaseExpanded items={items} onRestore={refetch} />
              )}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export { DeletedSanitizedCases };
